/**
 * 회원가입 페이지 (그룹 관리자용)
 */

import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Alert,
  Space,
  message,
  Progress,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { SignupRequest } from '../../types/auth';
import './AuthPages.css';

const { Title, Text } = Typography;

/**
 * 비밀번호 강도 체크
 */
const checkPasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^A-Za-z0-9]/.test(password)) strength += 10;
  return Math.min(strength, 100);
};

/**
 * 회원가입 페이지 컴포넌트
 */
export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signup, isLoading, error } = useAuth();
  const [form] = Form.useForm();
  const [passwordStrength, setPasswordStrength] = useState(0);

  /**
   * 비밀번호 변경 핸들러
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setPasswordStrength(checkPasswordStrength(password));
  };

  /**
   * 회원가입 제출 핸들러
   */
  const handleSubmit = async (values: SignupRequest) => {
    try {
      await signup(values);

      message.success('회원가입이 완료되었습니다! 로그인해주세요.');

      // 2초 후 로그인 페이지로 이동
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      // 에러는 useAuth에서 처리됨
      console.error('Signup failed:', err);
    }
  };

  return (
    <div className="auth-container">
      <Card className="auth-card" variant="borderless">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 로고 및 타이틀 - 로고 클릭 시 랜딩페이지로 이동 */}
          <div className="auth-header">
            <Title
              level={3}
              style={{
                margin: 0,
                marginBottom: '8px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              📚 채점톡
            </Title>
            <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
              회원가입
            </Title>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              학원 관리자 계정 생성
            </Text>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <Alert
              message="회원가입 실패"
              description={error}
              type="error"
              showIcon
              closable
            />
          )}

          {/* 회원가입 폼 */}
          <Form
            form={form}
            name="signup"
            onFinish={handleSubmit}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            {/* 학원 정보 */}
            <Title level={5} style={{ marginTop: 0 }}>
              학원 정보
            </Title>

            <Form.Item
              name="group_name"
              label="학원 이름"
              rules={[
                { required: true, message: '학원 이름을 입력해주세요' },
                { min: 2, max: 50, message: '학원 이름은 2-50자여야 합니다' },
              ]}
              hasFeedback
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input prefix={<HomeOutlined />} placeholder="예: 명문수학학원" />
            </Form.Item>


            {/* 관리자 정보 */}
            <Title level={5} style={{ marginTop: 16 }}>
              관리자 정보
            </Title>

            <Form.Item
              name="name"
              label="이름"
              rules={[
                { required: true, message: '이름을 입력해주세요' },
                { min: 2, max: 20, message: '이름은 2-20자여야 합니다' },
              ]}
              hasFeedback
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input prefix={<UserOutlined />} placeholder="홍길동" />
            </Form.Item>

            <Form.Item
              name="email"
              label="이메일"
              rules={[
                { required: true, message: '이메일을 입력해주세요' },
                { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
              ]}
              hasFeedback
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="admin@example.com"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="phone"
              label="전화번호"
              tooltip="선택사항입니다"
              validateTrigger="onBlur"
              rules={[
                {
                  required: false,
                  pattern: /^\d{3}-\d{4}-\d{4}$/,
                  message: '010-0000-0000 형식으로 입력해주세요',
                },
              ]}
              hasFeedback
            >
              <Input
                prefix={<PhoneOutlined />}
                placeholder="010-0000-0000 (선택사항)"
                autoComplete="tel"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="비밀번호"
              rules={[
                { required: true, message: '비밀번호를 입력해주세요' },
                { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' },
              ]}
              hasFeedback
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="비밀번호 (최소 6자)"
                autoComplete="new-password"
                onChange={handlePasswordChange}
              />
            </Form.Item>

            {/* 비밀번호 강도 표시 */}
            {passwordStrength > 0 && (
              <Form.Item>
                <Progress
                  percent={passwordStrength}
                  strokeColor={
                    passwordStrength < 40
                      ? '#ff4d4f'
                      : passwordStrength < 70
                        ? '#faad14'
                        : '#52c41a'
                  }
                  showInfo={false}
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {passwordStrength < 40
                    ? '약한 비밀번호'
                    : passwordStrength < 70
                      ? '보통 비밀번호'
                      : '강한 비밀번호'}
                </Text>
              </Form.Item>
            )}

            <Form.Item
              name="confirm_password"
              label="비밀번호 확인"
              dependencies={['password']}
              rules={[
                { required: true, message: '비밀번호를 다시 입력해주세요' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('비밀번호가 일치하지 않습니다'));
                  },
                }),
              ]}
              hasFeedback
              validateTrigger={['onChange', 'onBlur']}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="비밀번호 확인"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                style={{ marginTop: '8px' }}
              >
                회원가입
              </Button>
            </Form.Item>
          </Form>

          {/* 하단 링크 */}
          <div className="auth-footer">
            <Text type="secondary">이미 계정이 있으신가요? </Text>
            <Link to="/login">
              <Text>로그인</Text>
            </Link>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default SignupPage;
